/**
 * Single-handle deferred-work helper used by `GvgPlannerView` for the
 * screen-reader live-region announcements and the export-click
 * `URL.revokeObjectURL` deferral.
 *
 * Centralizes three concerns:
 *
 * 1. A single timeout handle (`pendingTimeout`), so a fast
 *    `scheduleTimeout → scheduleTimeout → ...` burst keeps only the *last*
 *    callback alive. This matters because the live-region announcement gets
 *    re-set on every mutation; clearing the previous timer prevents a stale
 *    "cleared" callback from wiping a fresher message.
 * 2. A `pendingTimeout` ref exposed to callers that want to cancel
 *    manually (e.g. the live-sync-error watcher nulls out the handle when
 *    the store clears the error).
 * 3. The `onBeforeUnmount` cleanup that drops any pending callback so a
 *    fast navigation away from the view doesn't fire a callback against an
 *    unmounted component.
 *
 * Plus a thin `announce(message, ms)` helper that bundles the
 * "set + auto-clear" pair — every GvG mutation handler uses this exact
 * pattern and would otherwise copy-paste the `scheduleTimeout` block four
 * times.
 */
import {
  onBeforeUnmount,
  ref,
  type Ref,
} from 'vue'

export interface UseViewTimeoutsReturn {
  /** Latest announcement text — bind to a `role="status"` / `aria-live="polite"` element. */
  readonly liveAnnouncement: Ref<string>
  /** The currently-pending timer handle, or `null`. Exposed so callers can cancel manually. */
  readonly pendingTimeout: Ref<ReturnType<typeof setTimeout> | null>
  /**
   * Replace the pending callback with `cb`, running after `ms` ms. Safe to
   * call on every keystroke; only the most-recently-scheduled callback is
   * kept alive.
   */
  readonly scheduleTimeout: (cb: () => void, ms: number) => void
  /** Cancel any pending callback without scheduling a replacement. */
  readonly clearPendingTimeout: () => void
  /**
   * Convenience: write `message` to `liveAnnouncement` and auto-clear it
   * after `durationMs` ms (defaults to the constructor's `defaultDurationMs`,
   * or 1500 when no default was passed). Browsers won't re-announce identical
   * text so the explicit clear-then-write dance is required on repeat
   * presses of the same affordance.
   */
  readonly announce: (message: string, durationMs?: number) => void
}

/**
 * Build the timeout helpers. The optional `defaultDurationMs` becomes the
 * no-arg default for `announce` — preserves the existing 1500 ms the live
 * region uses today.
 */
export function useViewTimeouts(defaultDurationMs = 1500): UseViewTimeoutsReturn {
  /** Last ephemeral toast-like message surfaced through the screen-reader live region. */
  const liveAnnouncement = ref<string>('')

  /**
   * Single timeout handle for the live-region auto-clear and the export
   * `URL.revokeObjectURL` defer. Tracked so `onBeforeUnmount` can cancel
   * pending work — without this, a fast navigation away during a download
   * click or right after an add would fire the callback against an unmounted
   * view.
   */
  const pendingTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  function scheduleTimeout(handler: () => void, delayMs: number): void {
    if (pendingTimeout.value !== null) {
      clearTimeout(pendingTimeout.value)
    }
    pendingTimeout.value = setTimeout(() => {
      pendingTimeout.value = null
      handler()
    }, delayMs)
  }

  function clearPendingTimeout(): void {
    if (pendingTimeout.value !== null) {
      clearTimeout(pendingTimeout.value)
      pendingTimeout.value = null
    }
  }

  function announce(message: string, durationMs: number = defaultDurationMs): void {
    liveAnnouncement.value = message
    scheduleTimeout(() => {
      liveAnnouncement.value = ''
    }, durationMs)
  }

  // Drop any pending work the moment the owning view unmounts so a slow
  // `URL.revokeObjectURL` defer (or any future scheduled mutation) never
  // writes into an unmounted component tree.
  onBeforeUnmount(() => {
    clearPendingTimeout()
  })

  return {
    liveAnnouncement,
    pendingTimeout,
    scheduleTimeout,
    clearPendingTimeout,
    announce,
  }
}
